// src/pages/GroupPage.js
// Apple Dark Mode Style
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GroupInfoSection from '../components/group/GroupInfoSection';
import MembersSection from '../components/group/MembersSection';
import NextMeetingSection from '../components/group/NextMeetingSection';
import ScheduleSection from '../components/group/ScheduleSection';
import SettingsSection from '../components/group/SettingsSection';

import './GroupPage.css';
import API_URL from '../config';

function GroupPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const token = localStorage.getItem('token');
  const userPlan = localStorage.getItem('userPlan') || 'free';

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [sectionsOpen, setSectionsOpen] = useState({
    basic: false,
    members: false,
    nextMeeting: false,
    schedule: false,
    settings: false
  });

  const [basicSettings, setBasicSettings] = useState({
    groupName: '',
    meetingType: 'recurring',
    meetingDay: '',
    meetingDate: '',
    meetingTime: '',
    threshold: '',
    maxParticipants: null,
    city: '',
    location: ''
  });

  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberModal, setShowMemberModal] = useState(false);

  const [advancedSettings, setAdvancedSettings] = useState({
    invitationAdvance: 7,
    reminderAdvance: 3,
    decisionTime: '18:00',
    timezone: 'Europe/Stockholm'
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    
    if (groupId === 'new') {
      createNewGroup();
    } else {
      fetchGroup();
    }
  }, [groupId, token, navigate]);

  // Auto-open basic settings for incomplete groups
  useEffect(() => {
    if (group && !loading) {
      const isComplete = group.groupName && 
                        group.threshold && 
                        (group.meetingType === 'single' 
                          ? group.singleMeetingDate 
                          : (group.meetingFrequency?.dayOfWeek && group.meetingFrequency?.time));
      
      if (!isComplete && !sectionsOpen.basic) {
        setSectionsOpen(prev => ({ ...prev, basic: true }));
      }
    }
  }, [group, loading, sectionsOpen.basic]);

  // Auto-open schedule section if coming from Wizard
  useEffect(() => {
    if (location.state?.openScheduleSection && !loading && group) {
      setSectionsOpen(prev => ({ ...prev, schedule: true }));
      // Clear the state to prevent re-opening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.openScheduleSection, loading, group]);

  const fetchUserLocation = async (newGroupId) => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      const updateResponse = await fetch(`${API_URL}/api/groups/${newGroupId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          city: data.city || '',
          country: data.country_name || '',
          timezone: data.timezone || 'Europe/Stockholm'
        })
      });

      if (updateResponse.ok) {
        const updatedGroup = await updateResponse.json();
        
        setGroup(updatedGroup);
        setBasicSettings(prev => ({
          ...prev,
          city: updatedGroup.city || '',
          location: updatedGroup.location || ''
        }));
        setAdvancedSettings(prev => ({
          ...prev,
          timezone: updatedGroup.timezone || 'Europe/Stockholm'
        }));
      }
      
    } catch (error) {
      console.error('Could not fetch location from IP:', error);
    }
  };

  const createNewGroup = async () => {
    try {
      const response = await fetch(`${API_URL}/api/groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          groupName: t('nav.newGroupDefault'),
          status: 'draft',
          threshold: null,
          city: '',
          location: '',
          meetingType: 'recurring',
          meetingFrequency: {
            dayOfWeek: '',
            time: ''
          }
        })
      });

      if (response.ok) {
        const newGroup = await response.json();
        navigate(`/groups/${newGroup._id}`, { replace: true });
        setGroup(newGroup);
        setBasicSettings({
          groupName: newGroup.groupName || '',
          meetingType: newGroup.meetingType || 'recurring',
          meetingDay: newGroup.meetingFrequency?.dayOfWeek || '',
          meetingDate: newGroup.singleMeetingDate || '',
          meetingTime: newGroup.meetingFrequency?.time || '',
          threshold: newGroup.threshold || '',
          maxParticipants: newGroup.maxParticipants || null,
          city: newGroup.city || '',
          location: newGroup.location || ''
        });
        setLoading(false);
        
        fetchUserLocation(newGroup._id);
        
      } else {
        const data = await response.json();
        alert(data.message);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      alert(t('groupPage.errors.couldNotCreateGroup'));
      navigate('/dashboard');
    }
  };

  const fetchGroup = async () => {
    try {
      const response = await fetch(`${API_URL}/api/groups/${groupId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Group not found');
      
      const data = await response.json();
      
      setGroup(data);
      setBasicSettings({
        groupName: data.groupName || '',
        meetingType: data.meetingType || 'recurring',
        meetingDay: data.meetingFrequency?.dayOfWeek || '',
        meetingDate: data.singleMeetingDate || '',
        meetingTime: data.meetingFrequency?.time || '',
        threshold: data.threshold || '',
        maxParticipants: data.maxParticipants || null,
        city: data.city || '',
        location: data.location || ''
      });
      setAdvancedSettings({
        invitationAdvance: data.invitationAdvance || 7,
        reminderAdvance: data.reminderAdvance || 3,
        decisionTime: data.decisionTime || '18:00',
        timezone: data.timezone || 'Europe/Stockholm'
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching group:', error);
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setSectionsOpen(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleBasicUpdate = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    const updatePayload = {
      groupName: basicSettings.groupName,
      threshold: parseInt(basicSettings.threshold),
      maxParticipants: basicSettings.maxParticipants ? parseInt(basicSettings.maxParticipants) : null,
      city: basicSettings.city,
      location: basicSettings.location,
      meetingType: basicSettings.meetingType
    };

    if (basicSettings.meetingType === 'recurring') {
      updatePayload.meetingFrequency = {
        dayOfWeek: basicSettings.meetingDay,
        time: basicSettings.meetingTime
      };
    } else {
      updatePayload.singleMeetingDate = basicSettings.meetingDate;
    }

    try {
      const response = await fetch(`${API_URL}/api/groups/${groupId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatePayload)
      });

      if (response.ok) {
        await fetchGroup();
        alert(t('groupPage.success.basicInfoUpdated'));
        window.dispatchEvent(new Event('groupsUpdated'));
        setSectionsOpen(prev => ({ ...prev, basic: false }));
      }
    } catch (error) {
      console.error('Error updating group:', error);
    }
  };

  const handleAdvancedUpdate = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    try {
      const updatePayload = {
        invitationAdvance: parseInt(advancedSettings.invitationAdvance),
        reminderAdvance: parseInt(advancedSettings.reminderAdvance),
        decisionTime: advancedSettings.decisionTime,
        timezone: advancedSettings.timezone
      };

      await fetch(`${API_URL}/api/groups/${groupId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatePayload)
      });
      
      alert(t('groupPage.success.settingsUpdated'));
      window.dispatchEvent(new Event('groupsUpdated'));
      fetchGroup();
    } catch (error) {
      console.error('Error updating advanced settings:', error);
    }
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm(t('groupPage.confirm.deleteGroup'))) return;
    
    try {
      const response = await fetch(`${API_URL}/api/groups/${groupId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        window.dispatchEvent(new Event('groupsUpdated'));
        alert(t('groupPage.success.groupDeleted'));
        navigate('/dashboard');
      } else {
        alert(t('groupPage.errors.couldNotDeleteGroup'));
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      alert(t('groupPage.errors.somethingWentWrong'));
    }
  };

  const openMemberModal = (member) => {
    setSelectedMember(member);
    setShowMemberModal(true);
  };

  const handleUpdateMember = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    try {
      await fetch(`${API_URL}/api/groups/${groupId}/participants/${selectedMember._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(selectedMember)
      });
      fetchGroup();
      setShowMemberModal(false);
      alert(t('groupPage.success.memberUpdated'));
    } catch (error) {
      console.error('Error updating member:', error);
    }
  };

  const handleDeleteMember = async () => {
    if (!window.confirm(t('groupPage.confirm.deleteMember'))) return;
    
    try {
      await fetch(`${API_URL}/api/groups/${groupId}/participants/${selectedMember._id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchGroup();
      setShowMemberModal(false);
      alert(t('groupPage.success.memberDeleted'));
    } catch (error) {
      console.error('Error deleting member:', error);
    }
  };

  if (loading) {
    return (
      <div className="group-page">
        <div className="loading">{t('groupPage.loading')}</div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="group-page">
        <div className="error">{t('groupPage.groupNotFound')}</div>
      </div>
    );
  }

  // Check if group is complete
  const isGroupComplete = group.groupName && 
                         group.threshold && 
                         (group.meetingType === 'single' 
                           ? group.singleMeetingDate 
                           : (group.meetingFrequency?.dayOfWeek && group.meetingFrequency?.time));

  return (
    <div className="group-page">
      <div className="group-content">

        {/* BASIC SETTINGS - Always accessible */}
        <GroupInfoSection
          group={group}
          basicSettings={basicSettings}
          setBasicSettings={setBasicSettings}
          onUpdate={handleBasicUpdate}
          isOpen={sectionsOpen.basic}
          onToggle={() => toggleSection('basic')}
        />

        {/* Locked sections wrapper */}
        <div style={{ position: 'relative' }}>
          {/* All sections with blur when group is incomplete */}
          <div className={isGroupComplete ? 'sections-unlocked' : 'sections-locked'}>
            {/* MEMBERS */}
            <MembersSection
              group={group}
              groupId={groupId}
              token={token}
              apiUrl={API_URL}
              userPlan={userPlan}
              onMemberClick={openMemberModal}
              onUpdated={fetchGroup}
              isOpen={sectionsOpen.members}
              onToggle={() => toggleSection('members')}
            />

            {/* NEXT MEETING */}
            <NextMeetingSection
              group={group}
              groupId={groupId}
              token={token}
              apiUrl={API_URL}
              isOpen={sectionsOpen.nextMeeting}
              onToggle={() => toggleSection('nextMeeting')}
            />

            {/* SCHEDULE */}
            <ScheduleSection
              group={group}
              groupId={groupId}
              token={token}
              apiUrl={API_URL}
              onUpdated={fetchGroup}
              isOpen={sectionsOpen.schedule}
              onToggle={() => toggleSection('schedule')}
            />

            {/* SETTINGS */}
            <SettingsSection
              group={group}
              groupId={groupId}
              token={token}
              apiUrl={API_URL}
              advancedSettings={advancedSettings}
              setAdvancedSettings={setAdvancedSettings}
              onUpdate={handleAdvancedUpdate}
              onDelete={handleDeleteGroup}
              isOpen={sectionsOpen.settings}
              onToggle={() => toggleSection('settings')}
            />
          </div>

          {/* Lock overlay */}
          {!isGroupComplete && (
            <div className="lock-overlay">
              <div className="lock-overlay-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              </div>
              <div className="lock-overlay-title">
                {t('groupPage.locked.title')}
              </div>
              <div className="lock-overlay-text">
                {t('groupPage.locked.fillBasicFirst')}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Member Modal */}
      {showMemberModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowMemberModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowMemberModal(false)}>×</button>
            <h2>{selectedMember.name}</h2>
            <form onSubmit={handleUpdateMember}>
              <div className="form-group">
                <label>{t('groupPage.modal.name')}</label>
                <input
                  type="text"
                  value={selectedMember.name}
                  onChange={(e) => setSelectedMember({...selectedMember, name: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>{t('groupPage.modal.email')}</label>
                <input
                  type="email"
                  value={selectedMember.email}
                  onChange={(e) => setSelectedMember({...selectedMember, email: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('groupPage.modal.phone')}</label>
                <input
                  type="tel"
                  placeholder={t('groupPage.modal.addPlaceholder')}
                  value={selectedMember.phone || ''}
                  onChange={(e) => setSelectedMember({...selectedMember, phone: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>{t('groupPage.modal.address')}</label>
                <input
                  type="text"
                  placeholder={t('groupPage.modal.addPlaceholder')}
                  value={selectedMember.address || ''}
                  onChange={(e) => setSelectedMember({...selectedMember, address: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>{t('groupPage.modal.city')}</label>
                <input
                  type="text"
                  placeholder={t('groupPage.modal.addPlaceholder')}
                  value={selectedMember.city || ''}
                  onChange={(e) => setSelectedMember({...selectedMember, city: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>{t('groupPage.modal.status')}</label>
                <select
                  value={selectedMember.status || 'active'}
                  onChange={(e) => setSelectedMember({...selectedMember, status: e.target.value})}
                >
                  <option value="active">{t('groupPage.modal.statusActive')}</option>
                  <option value="inactive">{t('groupPage.modal.statusInactive')}</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-save">{t('groupPage.modal.save')}</button>
                <button type="button" onClick={handleDeleteMember} className="btn-delete">
                  {t('groupPage.modal.delete')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default GroupPage;
